/**
 * PlayerExtras — 播放器下排「額外」掣（設計 v2 §4 C11 extras；UI v2 步 8）
 *
 *   ♥ 我的最愛／速度／音量／高音質／加入播放清單 ＋〔加入播放清單〕彈窗
 *   ⚠️ ♥ 狀態**照實讀**（`memberships()`）＋ 做唔到**照實報**（唔准扮成功）
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlayer } from './PlayerContext';
import { usePlaylists } from '../hooks/usePlaylists';
import { FAVOURITE_SLUG } from '../playlists';
import AddToPlaylist from '../components/AddToPlaylist';
import { Icons } from '../components/Icons';

export default function PlayerExtras({ onNote }: { onNote: (msg: string | null) => void }) {
  const { t } = useTranslation();
  const p = usePlayer();
  const { playlists, memberships, addSong, removeSong } = usePlaylists();
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [favOn, setFavOn] = useState(false);

  const track = p.track;
  const favouriteId = playlists.find((pl) => pl.slug === FAVOURITE_SLUG)?.id ?? null;

  /** ♥ 狀態照實讀 */
  useEffect(() => {
    if (!track || !favouriteId) {
      setFavOn(false);
      return;
    }
    let alive = true;
    const songId = track.id;
    void (async () => {
      const ids = await memberships(songId);
      if (alive) setFavOn(Boolean(ids?.includes(favouriteId)));
    })();
    return () => {
      alive = false;
    };
  }, [favouriteId, memberships, track]);

  const toggleFav = async () => {
    if (!track || !favouriteId) return;
    const wasOn = favOn;
    const res = wasOn ? await removeSong(favouriteId, track.id) : await addSong(favouriteId, track.id);
    if (!res.ok) {
      onNote(t('playlist_action_failed', { error: res.error ?? '' }));
      return;
    }
    setFavOn(!wasOn);
    onNote(wasOn ? t('playlist_removed_note', { name: t('pl_favourite') }) : t('playlist_added_note', { name: t('pl_favourite') }));
  };

  if (!track) return null;

  return (
    <div className="player__extras" data-testid="player-modes">
      <button
        type="button"
        data-testid="player-fav"
        className={'icon-btn' + (favOn ? ' icon-btn--on' : '')}
        aria-pressed={favOn}
        aria-label={favOn ? t('player_fav_remove') : t('player_fav_add')}
        onClick={() => void toggleFav()}
      >
        {favOn ? <Icons.HeartFill /> : <Icons.Heart />}
      </button>
      <button type="button" className="chip" onClick={p.cycleSpeed} aria-label={t('player_speed')}>
        {p.speed}×
      </button>
      <label className="volume">
        <Icons.Volume size="1.25rem" />
        <input
          type="range"
          min={0}
          max={100}
          value={p.volume}
          onChange={(e) => p.setVolume(Number(e.target.value))}
          aria-label={t('player_volume')}
        />
      </label>
      <button
        type="button"
        className={'icon-btn' + (p.hifi ? ' icon-btn--on' : '')}
        aria-pressed={p.hifi}
        aria-label={t('variant_hifi')}
        onClick={() => {
          if (!p.setHifi(!p.hifi)) onNote(t('player_hifi_off'));
        }}
      >
        <Icons.Check />
      </button>
      <button type="button" className="icon-btn" onClick={() => setPlaylistOpen(true)} aria-label={t('playlist_add_button')}>
        <Icons.PlaylistAdd />
      </button>

      <AddToPlaylist
        open={playlistOpen}
        songId={playlistOpen ? track.id : null}
        onClose={() => setPlaylistOpen(false)}
        onChanged={() => onNote(null)}
      />
    </div>
  );
}
